import { EmailLanguage, EmailFrequency } from './email';
import { availableCredits, type CreditBalance } from './credits';

export type EmailNotificationType =
  | 'global_report'
  | 'score_report'
  | 'competitor_news'
  | 'score_degradation'
  | 'tasks_recos'
  | 'traaker_news'
  | 'geo_news'
  | 'trial_lifecycle'
  | 'credit_alert'
  | 'credit_expiry_warning'
  | 'trial_credit_warning'
  | 'audit_failed';

export type NotificationTriggerKind = 'scheduled' | 'event' | 'lifecycle' | 'broadcast';

/** Reuses EmailFrequency's values (daily/weekly/monthly) under the registry's own name. */
export type NotificationCadence = EmailFrequency;

export interface NotificationTypeDef {
  type: EmailNotificationType;
  trigger: NotificationTriggerKind;
  scope: 'brand' | 'tenant';
  visibleInSettings: boolean;
  cadences?: NotificationCadence[];
  defaultCadence?: NotificationCadence;
  defaultEnabled: boolean;
  /** Default true. audit_failed sets false — factual list, no Bedrock call (plan §14.2, D8). */
  aiCommentary?: boolean;
  /**
   * Hard ceiling on ACTUAL sends per calendar month, counted per `lastSent` key
   * (so per brand for a brand-scoped type). Only `audit_failed` sets it: an
   * always-on daily digest of a persistently failing audit would mail the same
   * list 30 times a month, which reads as broken rather than urgent. Counting
   * SENDS, not scheduled ticks, is the point — the digest renders nothing on
   * days with no failures, and those days must not burn a slot.
   *
   * Enforced by the notification-worker via {@link monthlySendCapReached} /
   * {@link recordMonthlySend}; absent ⇒ no cap.
   */
  maxSendsPerMonth?: number;
}

const SCHEDULED_CADENCES: NotificationCadence[] = ['daily', 'weekly', 'monthly'];

export const NOTIFICATION_REGISTRY: Record<EmailNotificationType, NotificationTypeDef> = {
  global_report: {
    type: 'global_report',
    trigger: 'scheduled',
    scope: 'brand',
    visibleInSettings: true,
    cadences: SCHEDULED_CADENCES,
    defaultCadence: 'weekly',
    defaultEnabled: false,
  },
  score_report: {
    type: 'score_report',
    trigger: 'scheduled',
    scope: 'brand',
    visibleInSettings: true,
    cadences: SCHEDULED_CADENCES,
    defaultCadence: 'weekly',
    defaultEnabled: false,
  },
  competitor_news: {
    type: 'competitor_news',
    trigger: 'scheduled',
    scope: 'brand',
    visibleInSettings: true,
    cadences: SCHEDULED_CADENCES,
    defaultCadence: 'weekly',
    defaultEnabled: false,
  },
  score_degradation: {
    type: 'score_degradation',
    trigger: 'event',
    scope: 'brand',
    visibleInSettings: true,
    defaultEnabled: false,
  },
  tasks_recos: {
    type: 'tasks_recos',
    trigger: 'scheduled',
    scope: 'brand',
    visibleInSettings: true,
    cadences: SCHEDULED_CADENCES,
    defaultCadence: 'weekly',
    defaultEnabled: false,
  },
  traaker_news: {
    type: 'traaker_news',
    trigger: 'broadcast',
    scope: 'tenant',
    // No backend yet (broadcasts are unbuilt, plan §3) — flip to true when the
    // admin-authored broadcast pipeline ships. Until then, NotificationsSettings
    // derives its card list from this field, so leaving it true here would show
    // a toggle for a type that can never actually fire.
    visibleInSettings: false,
    defaultEnabled: false,
  },
  geo_news: {
    type: 'geo_news',
    trigger: 'broadcast',
    scope: 'tenant',
    visibleInSettings: false,
    defaultEnabled: false,
  },
  trial_lifecycle: {
    type: 'trial_lifecycle',
    trigger: 'lifecycle',
    scope: 'tenant',
    visibleInSettings: false,
    defaultEnabled: true,
  },
  credit_alert: {
    type: 'credit_alert',
    trigger: 'event',
    scope: 'tenant',
    visibleInSettings: true,
    defaultEnabled: false,
  },
  credit_expiry_warning: {
    type: 'credit_expiry_warning',
    // Same hidden auto-subscribed channel as `trial_lifecycle`: driven by its own
    // monthly cron (not the sweep, not a cadence), mailed to every validated user
    // of the tenant, and opted OUT of by clicking unsubscribe. There is nothing to
    // tune, so no settings card.
    trigger: 'lifecycle',
    scope: 'tenant',
    visibleInSettings: false,
    defaultEnabled: true,
  },
  trial_credit_warning: {
    type: 'trial_credit_warning',
    // Third hidden auto-subscribed channel, alongside `trial_lifecycle` and
    // `credit_expiry_warning`: own daily pass, mailed to every validated user of
    // the tenant, opted OUT of by clicking unsubscribe. Nothing to tune.
    //
    // NOT capped via `maxSendsPerMonth`: the ceiling here is a LIFETIME count of
    // 4 (see TRIAL_CREDIT_WARNING_MAX_SENDS), and the worker's monthly-cap gate
    // sits after the lifecycle branch has already returned anyway. The cap is
    // enforced in the cron service's claim instead.
    trigger: 'lifecycle',
    scope: 'tenant',
    visibleInSettings: false,
    defaultEnabled: true,
  },
  audit_failed: {
    type: 'audit_failed',
    trigger: 'scheduled',
    scope: 'brand',
    visibleInSettings: true,
    // NOT user-selectable (2026-08-27): a failure notice is a fault report, not a
    // digest you tune — nobody wants to hear about a broken audit monthly. It
    // stays on the daily tick (so at most one email per brand per day, carrying
    // every failure of the window) and is bounded by `maxSendsPerMonth` instead.
    // Kept as a one-element list rather than dropped so `selectCadences` /
    // `isScheduledTypeDue` still resolve a cadence, and the settings UI hides the
    // selector on `cadences.length < 2`.
    cadences: ['daily'],
    defaultCadence: 'daily',
    defaultEnabled: false,
    aiCommentary: false,
    maxSendsPerMonth: 5,
  },
};

export interface NotificationRecipient {
  userId: string;
  language?: EmailLanguage;
  /** Per-recipient cadence override; falls back to the type-level cadence when unset. */
  cadence?: NotificationCadence;
  /**
   * Which brands this recipient wants in the stacked email (2026-08-27).
   * ABSENT or EMPTY means "every brand of the tenant" — including brands created
   * later, which is why absence is the default rather than a materialized list of
   * every current brand (that would silently stop covering new brands).
   *
   * Only meaningful for brand-scoped scheduled types: a tenant-scoped type has no
   * brand to filter, and event types are dispatched per brand by their hook.
   * Validated against the tenant's brands on write; the worker treats an id that
   * no longer resolves as absent (deleted brand ⇒ no section, not an error).
   */
  brandIds?: string[];
}

export type DegradationScope =
  | 'website_overall'
  | 'bp_overall'
  | 'bp_axis'
  | 'bp_prompt'
  | 'earned_media_overall'
  | 'product_catalog_overall'
  | 'geo_visibility_measure';

export interface NotificationTypeConfig {
  enabled: boolean;
  cadence?: NotificationCadence;
  recipients: NotificationRecipient[];
  /**
   * Users who unsubscribed from a type whose recipients are IMPLICIT
   * (`trial_lifecycle`: the worker mails every validated tenant user, so there
   * is no `recipients` entry to drop). An explicit-recipient type expresses the
   * same thing by removing the entry from `recipients` and never sets this.
   */
  optedOutUserIds?: string[];
  channels?: { slack?: boolean; webhook?: boolean };
  degradation?: {
    thresholdPoints: number;
    consecutiveRuns: number;
    scopes: DegradationScope[];
  };
  creditAlert?: { thresholdCredits: number };
}

/** All `DegradationScope` values except `bp_prompt` (plan D3 — per-prompt defaults OFF, noise). */
export const DEFAULT_DEGRADATION_SCOPES: DegradationScope[] = [
  'website_overall',
  'bp_overall',
  'bp_axis',
  'earned_media_overall',
  'product_catalog_overall',
  'geo_visibility_measure',
];

/**
 * Plan D3 defaults for `score_degradation`/`credit_alert` sub-config. The
 * frontend and `EmailNotificationConfigService.updateType` both read these —
 * NOT independent `?? 5`/`?? 50` literals — so a user who enables a type with
 * no sub-config gets a rule that actually fires (fix for the 2026-08-21 audit
 * finding: evaluators bail out silently when the sub-config is absent).
 */
export const DEFAULT_DEGRADATION_CONFIG: NonNullable<NotificationTypeConfig['degradation']> = {
  thresholdPoints: 5,
  consecutiveRuns: 3,
  scopes: DEFAULT_DEGRADATION_SCOPES,
};

export const DEFAULT_CREDIT_ALERT_CONFIG: NonNullable<NotificationTypeConfig['creditAlert']> = {
  thresholdCredits: 50,
};

export const NOTIF_DUE_PK = 'NOTIF_DUE';
/** SK of the tenant's NOTIF_CONFIG row (PK = `TENANT#<id>`). */
export const NOTIF_CONFIG_SK = 'NOTIF_CONFIG';

/**
 * Tenant-level Slack integration (plan §13.2). One incoming-webhook URL per
 * tenant, toggled per type via `NotificationTypeConfig.channels.slack`.
 *
 * `webhookUrl` is a SECRET — possession of the URL is the only credential Slack
 * checks. It must never leave the API unmasked (`SlackConfigView`) and never
 * reach a log line.
 */
export interface SlackNotificationConfig {
  webhookUrl: string;
  addedByUserId: string;
  addedAt: string;
  /** Consecutive delivery failures; reset to 0 by the worker on any success. */
  failureCount?: number;
  /** Set by the worker after SLACK_BROKEN_AFTER consecutive failures (revoked URL). */
  broken?: boolean;
}

/** What the API returns instead of {@link SlackNotificationConfig}: no URL. */
export interface SlackConfigView {
  /** Domain + last 4, same masking idiom as `WebhookConfigView.secretHint`. */
  urlHint: string;
  addedByUserId: string;
  addedAt: string;
  broken?: boolean;
}

export interface TenantNotificationConfig {
  types: Partial<Record<EmailNotificationType, NotificationTypeConfig>>;
  slack?: SlackNotificationConfig;
  /** Sparse-GSI sort key (`notif-due-index`); present only while notifDuePK is set. */
  nextDueAt?: string;
  /**
   * `${type}` -> ISO timestamp. Scheduled types are keyed by type ALONE since
   * stacking (2026-08-27) — one email carries every brand, so there is one
   * cadence anchor per type. Event types (dispatched per brand by their hook)
   * still key `${type}#${brandId}`. Pre-stacking `${type}#${brandId}` entries for
   * scheduled types are inert orphans: the new key is absent, so the type reads
   * as due on the first sweep after deploy and sends once.
   */
  lastSent?: Record<string, string>;
  /**
   * Monthly send counters for types carrying `maxSendsPerMonth`, keyed exactly
   * like {@link lastSent} — so `audit_failed`'s 5/month is a TENANT-wide ceiling,
   * not per brand. `month` is `YYYY-MM`; a counter from a previous month is reset
   * rather than pruned (one row per capped type, so there is nothing to
   * garbage-collect).
   */
  sendCounts?: Record<string, { month: string; count: number }>;
  /**
   * `${type}` -> total sends EVER, for types capped over the lifetime of an
   * account rather than per month ({@link TRIAL_CREDIT_WARNING_MAX_SENDS}).
   * Separate from {@link sendCounts} on purpose: that one resets when the month
   * rolls over, which is exactly what a lifetime ceiling must not do. Never
   * pruned, and deliberately NOT reset when the condition clears and returns, so
   * a tenant who tops up and slides back under the floor cannot earn a fresh
   * batch of nags.
   */
  lifetimeSends?: Record<string, number>;
}

/** The client-safe projection of {@link TenantNotificationConfig}. */
export interface TenantNotificationConfigView extends Omit<TenantNotificationConfig, 'slack'> {
  slack?: SlackConfigView;
}

/** `https://hooks.slack.com/services/T…/B…/xxxx` → `hooks.slack.com/…xxxx`. */
export function maskSlackUrl(url: string): string {
  try {
    const { hostname } = new URL(url);
    return `${hostname}/…${url.slice(-4)}`;
  } catch {
    return `…${url.slice(-4)}`;
  }
}

export function toSlackConfigView(slack: SlackNotificationConfig): SlackConfigView {
  return {
    urlHint: maskSlackUrl(slack.webhookUrl),
    addedByUserId: slack.addedByUserId,
    addedAt: slack.addedAt,
    ...(slack.broken ? { broken: true } : {}),
  };
}

/**
 * Which notification types may fan out to non-email channels (plan §13.3):
 * broadcasts are marketing content and lifecycle is 1:1 onboarding copy — both
 * email-only. Drives which settings cards show channel toggles.
 */
export function supportsNonEmailChannels(type: EmailNotificationType): boolean {
  const trigger = NOTIFICATION_REGISTRY[type]?.trigger;
  return trigger === 'scheduled' || trigger === 'event';
}

export interface NotificationJob {
  /**
   * `render`   — scheduled type, sweep-dispatched, renderer table.
   * `event`    — hook-dispatched, evaluator table decides whether to send.
   * `lifecycle`— trial sequence (type 7) and the unused-credit warning (type 11).
   *              Bypasses NOTIF_CONFIG entirely (both types are
   *              `visibleInSettings: false` and the tenant may have no config row
   *              at all); recipients are the tenant's own validated users, minus
   *              `optedOutUserIds`. Email-only, always — see plan §13.3.
   */
  kind: 'render' | 'event' | 'lifecycle';
  type: EmailNotificationType;
  tenantId: string;
  /**
   * The single brand this job is about. Set by EVENT hooks (one alert = one
   * brand) and left unset by the sweep since 2026-08-27, which sends `brandIds`
   * instead. That absence is load-bearing: it makes both the `lastSent` key and
   * the per-recipient receipt key tenant-scoped (`type`, not `type#brandId`) for
   * stacked digests, while event notifications keep their per-brand keys.
   */
  brandId?: string;
  /**
   * Every brand the stacked digest may cover, in display order (sweep-dispatched
   * `render` jobs only). Recipients narrow it further via
   * {@link NotificationRecipient.brandIds}; the worker renders the union of what
   * its deliverable recipients actually want, never the whole list blindly.
   */
  brandIds?: string[];
  periodStart?: string;
  periodEnd?: string;
  event?: Record<string, unknown>;
  /** Which lifecycle step this job renders (`kind: 'lifecycle'` only). */
  step?: TrialLifecycleStep;
  triggeredFor: string;
}

/**
 * Trial-lifecycle sequence (type 7, plan §9). One-shot steps, driven by the
 * daily `reap-trials` cron — NOT by cadence and NOT by the sweep, which is why
 * `trial_lifecycle` is `trigger: 'lifecycle'` and invisible in settings.
 *
 * Order matters: `TRIAL_LIFECYCLE_STEPS` is walked in sequence and the FIRST due
 * step wins, so at most one lifecycle email per tenant per cron day. The order
 * is therefore a PRIORITY order, not a chronological one — every DEADLINE-driven
 * step (e3/e4/e5/e6, whose windows close) sits ahead of every SEQUENCE-driven one
 * (e0/e1/e2, due-forever-until-sent), because a sequence step that loses today is
 * merely late while a deadline step that loses today is lost for good. Before
 * 2026-08-24 the order was chronological and a tenant onboarding on day 6 got E1
 * on day 7 and never got E3 at all.
 */
export type TrialLifecycleStep =
  | 'e0_welcome'
  | 'e1_first_audit'
  | 'e2_recos'
  | 'e3_last_day'
  | 'e4_offer'
  | 'e5_winback'
  | 'e6_competitors'
  | 'e7_deletion_warning';

export const TRIAL_LIFECYCLE_STEPS: TrialLifecycleStep[] = [
  // Deadline-driven first — each of these has a window that closes.
  'e7_deletion_warning',
  'e3_last_day',
  'e4_offer',
  'e5_winback',
  'e6_competitors',
  // Sequence-driven: due until sent, so losing a day costs nothing.
  'e0_welcome',
  'e1_first_audit',
  'e2_recos',
];

/**
 * Steps whose window closes, in the priority order above. Kept as its own list so
 * a reader (and the service's tests) can see the two classes without re-deriving
 * them from the switch in `dueStep`.
 */
export const TRIAL_LIFECYCLE_DEADLINE_STEPS: TrialLifecycleStep[] = [
  'e7_deletion_warning',
  'e3_last_day',
  'e4_offer',
  'e5_winback',
  'e6_competitors',
];

/**
 * Day (relative to signup, i.e. `trialEndsAt - SELF_SERVE_TRIAL_DAYS`) the
 * mid-trial competitor beat fires on. Sits in the silent stretch between E2
 * (day ~2) and E3 (last day) and is skipped entirely when there's no competitor
 * data to show — see the renderer's `e6_competitors` branch.
 */
export const TRIAL_COMPETITOR_BEAT_DAY = 4;

/**
 * Days after `trialEndsAt` that the "we're about to delete your account" warning
 * goes out. TRIAL_REAPER_GRACE_DAYS is 15, so this leaves 3 days' notice. Only
 * fires for tenants that never onboarded (the same predicate the reaper uses) —
 * anyone else is never purged, so warning them would be a lie.
 */
export const TRIAL_DELETION_WARNING_DAY = 12;

/**
 * Per-step send stamps, on `tenant.settings.trialLifecycle`. A step is sent at
 * most once ever: the stamp is the idempotency record AND the E2 clock (E2 fires
 * one day after E1's stamp, not after a fixed offset from signup — a tenant that
 * never runs an audit never gets E1, so never gets E2).
 */
export interface TrialLifecycleState {
  sent?: Partial<Record<TrialLifecycleStep, string>>;
  /** Set by the audit-finalize hook the first time any audit completes. */
  firstAuditCompletedAt?: string;
  /** Brand whose first audit fired E1 — the renderer scopes its summary to it. */
  firstAuditBrandId?: string;
}

/** The promotion code E4/E5 hand out. See docs/billing/pricing-and-billing.md §4.9. */
export const TRIAL_OFFER_PROMO_CODE = 'TRIAL50';

/**
 * UTM campaign every trial-lifecycle CTA carries. The step itself goes in
 * `utm_content`, which is what makes the sequence tunable: GA4 attributes a
 * signup/conversion to the exact step that produced the click, and the same
 * value is sent to SES as the `step` message tag so open/click/bounce rates are
 * comparable per step. Built by `trialUtm()` so no template can drift.
 */
export const TRIAL_UTM_CAMPAIGN = 'trial_lifecycle';

/**
 * Append the sequence's UTMs to an in-app URL. `mailto:` and off-app links are
 * left untouched by the renderer (nothing consumes UTMs there).
 *
 * `utm_content` is the step, so per-step CTR is readable without a separate
 * naming convention per template; `slot` distinguishes the primary CTA from the
 * ghost/footnote links inside one email.
 */
export function trialUtm(url: string, step: TrialLifecycleStep, slot = 'cta'): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}utm_source=email&utm_medium=lifecycle&utm_campaign=${TRIAL_UTM_CAMPAIGN}&utm_content=${step}&utm_term=${slot}`;
}

// ───────── unused-subscription-credit warning (notification type 11) ─────────

/**
 * Day of the month the warning cron fires on. Agency monthly subscriptions are
 * calendar-aligned (`billing_cycle_anchor` is the 1st — see
 * docs/billing/pricing-and-billing.md), so whatever is left of the allocation is
 * REPLACED, not carried over, when the next invoice is paid. The 25th leaves a
 * usable week to spend it.
 */
export const CREDIT_EXPIRY_WARNING_DAY = 25;

/**
 * Share of `monthlyAllocation` that must still sit unused in
 * `subscriptionCredits` for the warning to be worth an email: 10%, i.e. 800 of an
 * 8 000-credit Agency S plan. Below that the leftover is too small to act on and
 * the email would be noise.
 */
export const CREDIT_EXPIRY_WARNING_RATIO = 0.1;

/**
 * The threshold, in ONE place: the cron service selects candidates with it and
 * the renderer re-checks it at send time (a tenant that burns the leftover
 * between the two gets no email). `topupCredits` are deliberately excluded — they
 * never expire, so they are not what this email is about.
 */
export function qualifiesForCreditExpiryWarning(balance: {
  subscriptionCredits?: number;
  monthlyAllocation?: number;
}): boolean {
  const allocation = balance.monthlyAllocation || 0;
  const unused = balance.subscriptionCredits || 0;
  return allocation > 0 && unused >= allocation * CREDIT_EXPIRY_WARNING_RATIO;
}

// ───────── trial low-credit warning (registry row 11) ─────────

/**
 * Available-credit floor under which a TRIALING agency gets warned. 150 of the
 * `AGENCY_TRIAL_CREDITS` (500) granted at signup, so roughly the last third.
 *
 * Sized to land while the trial can still act: the balance never reaches 0,
 * because a reserve that the balance cannot cover is REFUSED rather than
 * partially spent, so a trial with 40 credits left and a 90-credit audit due
 * simply plateaus there with its audits failing. Anything below this floor is
 * therefore effectively empty, which is why the copy warns about audits failing
 * rather than about hitting zero.
 */
export const TRIAL_CREDIT_WARNING_FLOOR = 150;

/**
 * LIFETIME ceiling, not monthly. Agency trials are never reaped (the reaper
 * range-queries the `trial-index` GSI on `trialEndsAt`, and an agency trial has
 * neither key — see docs/billing/signup-and-trials.md), so a `trialing` tenant
 * that abandons the product stays `trialing` forever. Without a hard cap this
 * type would mail an unread inbox weekly in perpetuity. 4 sends ≈ three weeks of
 * nudges.
 */
export const TRIAL_CREDIT_WARNING_MAX_SENDS = 4;

/** Minimum gap between two sends. Weekly, so the 4 sends span ~3 weeks. */
export const TRIAL_CREDIT_WARNING_INTERVAL_DAYS = 7;

/**
 * The floor, in ONE place: the daily pass selects candidates with it and the
 * renderer re-checks it at send time, so a trial that subscribes or gets a
 * top-up between the two gets no email.
 *
 * Tests the SPENDABLE balance (`availableCredits`, i.e. both buckets minus
 * reserved), not `subscriptionCredits` — a trial's grant lands in `topupCredits`
 * (it is a `manualGrant` and must not expire), so reading the subscription bucket
 * would see 0 for every trial and mail all of them on day one.
 */
export function qualifiesForTrialCreditWarning(balance: CreditBalance): boolean {
  return availableCredits(balance) < TRIAL_CREDIT_WARNING_FLOOR;
}

/**
 * Earliest nextDueAt across all enabled scheduled types (honoring per-recipient
 * cadence overrides — the tightest override wins so no recipient is under-served).
 * Returns undefined when no scheduled type is enabled (caller REMOVEs notifDuePK).
 */
export function computeNextDueAt(
  config: TenantNotificationConfig,
  registry: Record<EmailNotificationType, NotificationTypeDef>,
  now: Date,
): string | undefined {
  let earliest: number | undefined;

  for (const [type, typeConfig] of Object.entries(config.types) as Array<
    [EmailNotificationType, NotificationTypeConfig | undefined]
  >) {
    const def = registry[type];
    if (!def || def.trigger !== 'scheduled' || !typeConfig?.enabled) continue;

    for (const cadence of selectCadences(typeConfig, def)) {
      const dueMs = nextTickMs(cadence, now);
      if (earliest === undefined || dueMs < earliest) earliest = dueMs;
    }
  }

  return earliest === undefined ? undefined : new Date(earliest).toISOString();
}

/**
 * Whether one scheduled type (already known enabled) is due to fire right now,
 * anchored on ITS OWN last-send timestamp rather than the tenant-wide
 * `nextDueAt` — the fix for mixed-cadence tenants collapsing to the tightest
 * type (e.g. daily score_report firing the weekly global_report every day).
 * Never sent yet (`lastSentIso` undefined) is always due (first send).
 */
export function isScheduledTypeDue(
  typeConfig: NotificationTypeConfig,
  def: NotificationTypeDef,
  lastSentIso: string | undefined,
  now: Date,
): boolean {
  const cadence = tightestCadence(typeConfig, def);
  if (!cadence) return false;
  if (!lastSentIso) return true;
  return nextTickMs(cadence, new Date(lastSentIso)) <= now.getTime();
}

/** `YYYY-MM` — the bucket `sendCounts` counts within. */
export function sendCountMonth(now: Date): string {
  return now.toISOString().slice(0, 7);
}

/**
 * Whether a capped type has already used its month's budget for this key.
 * A counter stamped with a previous month never blocks — it's a stale bucket,
 * not a used one.
 */
export function monthlySendCapReached(
  config: Pick<TenantNotificationConfig, 'sendCounts'>,
  def: NotificationTypeDef,
  key: string,
  now: Date,
): boolean {
  if (!def.maxSendsPerMonth) return false;
  const counter = config.sendCounts?.[key];
  if (!counter || counter.month !== sendCountMonth(now)) return false;
  return counter.count >= def.maxSendsPerMonth;
}

function selectCadences(
  typeConfig: NotificationTypeConfig,
  def: NotificationTypeDef,
): NotificationCadence[] {
  return (typeConfig.recipients.length
    ? typeConfig.recipients.map((r) => r.cadence ?? typeConfig.cadence ?? def.defaultCadence)
    : [typeConfig.cadence ?? def.defaultCadence]
  )
    .filter((c): c is NotificationCadence => !!c)
    .map((c) => clampCadence(c, def));
}

/**
 * A STORED cadence the type no longer offers resolves to the type's default.
 * `audit_failed` was narrowed to `['daily']` on 2026-08-27, so every config
 * saved before that can hold `weekly`/`monthly` — and the settings UI now hides
 * the selector for it, leaving no way to correct the row by hand. Without this
 * clamp those tenants keep the old interval forever while both the registry and
 * the email footnote claim daily.
 *
 * Only a cadence that is REAL but not offered by this type gets clamped — a
 * value that isn't a cadence at all (`'hourly'`, a patched row) passes straight
 * through so `nextTickMs` still throws on it. Clamping those too would trade one
 * silent-wrong-interval bug for a silent-bad-data one.
 */
function clampCadence(
  cadence: NotificationCadence,
  def: NotificationTypeDef,
): NotificationCadence {
  if (!(cadence in CADENCE_INTERVAL_MS)) return cadence;
  if (!def.cadences || def.cadences.includes(cadence)) return cadence;
  return def.defaultCadence ?? def.cadences[0] ?? cadence;
}

function tightestCadence(
  typeConfig: NotificationTypeConfig,
  def: NotificationTypeDef,
): NotificationCadence | undefined {
  const cadences = selectCadences(typeConfig, def);
  if (!cadences.length) return undefined;
  return cadences.reduce((tightest, c) =>
    CADENCE_INTERVAL_MS[c] < CADENCE_INTERVAL_MS[tightest] ? c : tightest,
  );
}

/**
 * Rolling intervals, not calendar-anchored (weekly ≠ next Monday, monthly ≠
 * the 1st) — a deliberate simplicity choice over the plan's original
 * calendar-anchoring language; see docs/features/email-notifications.md.
 */
const CADENCE_INTERVAL_MS: Record<NotificationCadence, number> = {
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
  monthly: 30 * 24 * 60 * 60 * 1000,
};

function nextTickMs(cadence: NotificationCadence, now: Date): number {
  const interval = CADENCE_INTERVAL_MS[cadence];
  if (interval === undefined) {
    // Defense in depth: bad stored/patched data must fail loudly here, not
    // silently resolve to NaN → an invalid ISO string → the REMOVE branch that
    // strips notifDuePK and disables every scheduled type for the tenant.
    throw new Error(`Unknown notification cadence: ${cadence}`);
  }
  return now.getTime() + interval;
}
